/**
 * General Settings Page
 *
 * Manage basic workspace configuration (name, description).
 * Owner-only access.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { AlertTriangle, Building2, Save, Loader2, Trash2 } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
  Textarea,
} from '@loader/ui';

import { SettingsPageLayout } from '../components';

import { ConfirmDeleteDialog } from '@/components/common';
import { useWorkspace, useWorkspaceMembership } from '@/hooks';

interface WorkspaceFormData {
  name: string;
  description: string;
}

export function GeneralSettingsPage() {
  const navigate = useNavigate();
  const { workspace, isLoading, updateWorkspace, isUpdating, deleteWorkspace, isDeleting } = useWorkspace();
  const { isOwner, isLoading: isMembershipLoading } = useWorkspaceMembership();

  const [formData, setFormData] = useState<WorkspaceFormData>({
    name: '',
    description: '',
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Initialize form data from workspace when it loads
  useEffect(() => {
    if (workspace) {
      setFormData({
        name: workspace.name,
        description: workspace.description ?? '',
      });
    }
  }, [workspace]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!workspace) return;

    updateWorkspace({
      name: formData.name,
      description: formData.description || undefined,
    });
  };

  const handleDeleteWorkspace = async () => {
    deleteWorkspace();
    setDeleteDialogOpen(false);
    // Navigate to workspace selection after deletion
    void navigate({ to: '/workspace' });
  };

  return (
    <SettingsPageLayout
      title="General"
      description="Manage your workspace profile and settings"
      isLoading={isLoading || isMembershipLoading}
      isOwner={isOwner}
      requiresOwner={true}
    >
      <div className="space-y-6">
        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Workspace Profile
              </CardTitle>
              <CardDescription>
                Update your workspace name and description
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Workspace Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="My Workspace"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Describe your workspace (optional)"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, description: e.target.value }))
                  }
                  rows={3}
                />
              </div>
              <div className="flex justify-end">
                <Button type="submit" disabled={isUpdating || !formData.name.trim()} className="min-w-28">
                  {isUpdating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Save
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>

        {/* Danger Zone */}
        <Card className="border-destructive/50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div className="space-y-1">
              <CardTitle className="text-destructive flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Danger Zone
              </CardTitle>
              <CardDescription>
                Permanently delete this workspace and all of its data
              </CardDescription>
            </div>
            <Button
              variant="destructive"
              onClick={() => setDeleteDialogOpen(true)}
              disabled={isDeleting}
              className="min-w-28"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </>
              )}
            </Button>
          </CardHeader>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Workspace"
        warningMessage={`This will permanently delete "${workspace?.name}" and all of its data including tables, versions, linked tables, and API keys.`}
        isLoading={isDeleting}
        onConfirm={handleDeleteWorkspace}
      />
    </SettingsPageLayout>
  );
}
